import React, { useEffect, useState } from "react";
import { Button, message, Input, DatePicker, Select, Space } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import PurchaseForm from "./PurchaseDrawer";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { Outlet, useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getUserRole } from "../../utils/auth";

const { Option } = Select;

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function Purchase() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [stores, setStores] = useState([]);
  const [user, setUser] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    storeName: null,
    search: "",
    supplierName: "",
    productName: "",
    fromDate: null,
    toDate: null,
    financialYear: null, // New: Financial year filter
  });
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    setUserRole(getUserRole());
    getUserData();
    fetchStores();
  }, []);

  const navigate = useNavigate();

  // Fetch stores for the dropdown
  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
      const userData = JSON.parse(localStorage.getItem('userData')) || {};
      const userStoreId = userData.storeId;
      const userRoleValue = getUserRole();

      if (userRoleValue.role !== 'admin' && userStoreId) {
        const matchingStore = response.data.find((store) => store._id === userStoreId);
        if (matchingStore) {
          setFilters((prev) => ({ ...prev, storeName: matchingStore.name }));
        }
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to fetch stores.");
    }
  };

  // Generate financial years starting from 2024-2025 to current financial year (April-based)
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st

    let currentFinancialYearStart, currentFinancialYearEnd;
    if (currentDate < financialYearStart) {
      currentFinancialYearStart = currentDate.getFullYear() - 1;
      currentFinancialYearEnd = currentDate.getFullYear();
    } else {
      currentFinancialYearStart = currentDate.getFullYear();
      currentFinancialYearEnd = currentDate.getFullYear() + 1;
    }

    const financialYears = [];
    let startYear = 2024; // Start from 2024
    let endYear = startYear + 1;

    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }

    return financialYears; // Return in ascending order (oldest to newest)
  };

  const getPurchases = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      const { storeName, search, supplierName, productName, fromDate, toDate, financialYear } = filters;
      const response = await API.get("/purchase/paginate", {
        params: {
          page,
          limit: pageSize,
          storeName: storeName || undefined,
          search,
          supplierName,
          productName: productName || undefined,
          fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : null,
          toDate: toDate ? dayjs(toDate).format('YYYY-MM-DD') : null,
          financialYear: financialYear || null, // Pass financial year to API
        },
      });
      const { purchases: getPurchases, pagination } = response.data;

      setPurchases(getPurchases);
      setPaginationInfo({
        current: page,
        pageSize,
        total: pagination.total,
      });
    } catch (err) {
      console.error("Error fetching purchases:", err);
      message.error("Failed to load purchases.");
    } finally {
      setLoading(false);
    }
  };

  const getUserData = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userData'));
      setUser(userInfo);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getPurchases(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [filters, paginationInfo.current, paginationInfo.pageSize]);

  const handleSavePurchase = async (updatedPurchase) => {
    getPurchases(paginationInfo.current, paginationInfo.pageSize, filters);
    setOpenDrawer(false);
  };

  const handleEditPurchase = (purchase) => {
    setSelectedPurchase(purchase);
    setOpenDrawer(true);
  };

  const handleDeletePurchase = async () => {
    try {
      await API.delete(`/purchase/${purchaseToDelete}`);
      message.success("Purchase deleted successfully!");
      setPurchases((prev) =>
        prev.filter((purchase) => purchase?._id !== purchaseToDelete)
      );
      setIsDeleteModalVisible(false);
    } catch (error) {
      message.error(error.response?.data?.message);
    }
  };

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value || null,
    }));
  };

  const handleDownload = async (data) => {
    try {
      const result = await API.get(`/purchase/generate-invoice/${data?._id}`);
      if (result.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_${data?.invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to generate invoice.");
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Invoice No.",
      render: (_, purchase) => purchase?.invoiceId,
      key: "invoiceId",
      filterDropdown: () => (
        <Input
          placeholder="Search Invoice"
          onChange={(e) => handleSearchChange('search', e.target.value)}
          value={filters?.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Invoice Date",
      key: "orderDate",
      render: (_, purchase) => dayjs(purchase?.orderDate).format('DD-MM-YYYY'),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={selectedKeys && selectedKeys.length > 0 ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])] : []}
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")] : []);
            }}
            format="YYYY-MM-DD"
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 90, marginRight: 8 }}
              onClick={() => {
                confirm();
                if (selectedKeys && selectedKeys.length > 1) {
                  handleSearchChange("fromDate", selectedKeys[0]);
                  handleSearchChange("toDate", selectedKeys[1]);
                }
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearchChange("fromDate", null);
                handleSearchChange("toDate", null);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <SearchOutlined />,
    },
    {
      title: "Invoice Amount (in ₹)",
      key: "totalAmount",
      render: (_, purchase) => `₹ ${(new Intl.NumberFormat().format(purchase?.totalAmount))}`,
    },
    {
      title: "Taxable Amount",
      key: "totalAmountWithoutTax",
      render: (_, purchase) => `₹ ${(new Intl.NumberFormat().format(purchase?.totalAmount - purchase?.totalAmountWithoutTax))}`,
    },
    {
      title: "Supplier",
      render: (_, purchase) => purchase?.supplierId?.name,
      filterDropdown: () => (
        <Input
          placeholder="Search Supplier"
          onChange={(e) => handleSearchChange('supplierName', e.target.value)}
          value={filters?.supplierName}
          suffix={<SearchOutlined />}
        />
      ),
      key: "supplierName",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color;
        if (status === "pending") color = "yellow";
        else if (status === "completed") color = "green";
        else if (status === "cancelled") color = "red";
        return (
          <span style={{ color: color, fontWeight: "bold" }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
    {
      title: "Invoice Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
    },
    {
      title: "Paid Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
    },
    {
      title: "Balance Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, purchase) => (
        <div>
          {userRole.editPermission === true && (
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditPurchase(purchase)}
              style={{ marginRight: "8px", color: 'blue' }}
            />
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(purchase)}
            style={{ marginRight: "8px", color: 'green' }}
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          onClick={() => navigate("/purchaseForm")}
        >
          Add Purchase
        </Button>
        <Select
          placeholder="Select Store"
          value={filters.storeName}
          onChange={(value) => handleSearchChange("storeName", value)}
          style={{ width: 200 }}
          allowClear
          loading={loading}
                      disabled={userRole!='admin'}

        >
          {stores.map((store) => (
            <Option key={store._id} value={store.name}>
              {store.name}
            </Option>
          ))}
        </Select>
        <Input
          placeholder="Search Product"
          onChange={(e) => handleSearchChange('productName', e.target.value)}
          value={filters?.productName}
          suffix={<SearchOutlined />}
        />
        <Select
          placeholder="Select Financial Year"
          value={filters.financialYear}
          onChange={(value) => handleSearchChange("financialYear", value)}
          style={{ width: 200 }}
          allowClear
          loading={loading}
        >
          {generateFinancialYears().map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </Space>

      <CustomTable
        columns={columns}
        dataSource={purchases}
        onPaginationChange={(pagination) => {
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          });
        }}
        loading={loading}
        style={{ padding: '0 0px' }}
        total={paginationInfo.total}
      />

      <PurchaseForm
        open={openDrawer}
        setOpen={setOpenDrawer}
        data={selectedPurchase}
        onSave={handleSavePurchase}
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeletePurchase}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this Purchase? This action cannot be undone."
      />
    </div>
  );
}